import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "../site/SectionLabel";
import { HOME_SECTIONS } from "@/constants/testIds";
import { useWorks } from "@/lib/works";

export default function CaseStudies() {
  const items = (useWorks("?type=case_study") || []).slice(0, 6);
  return (
    <section
      data-testid={HOME_SECTIONS.cases}
      className="relative bg-ink-950 py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel number="08">Case Studies</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-5xl leading-[0.98] tracking-tighter text-ivory lg:text-7xl">
              Design decisions.<br />
              <span className="italic text-gold">Business outcomes.</span>
            </h2>
            <p className="mt-6 max-w-xl font-sans text-sm text-ivory-muted lg:text-base">
              See how strategy, design and execution come together to solve real business challenges.
            </p>
          </div>
          <Link
            to="/case-studies"
            className="group inline-flex items-center gap-3 self-start font-sans text-[12px] uppercase tracking-widest text-gold hover:text-ivory transition-colors md:self-end"
          >
            All case studies
            <span className="h-px w-8 bg-current transition-all group-hover:w-12" />
          </Link>
        </div>

        <div className="mt-16 divide-y divide-ivory/[0.06] border-y border-ivory/[0.06]">
          {items.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
            >
              <Link
                to={c.href}
                className="group grid grid-cols-1 items-center gap-6 py-8 lg:grid-cols-12 lg:gap-8 lg:py-10"
              >
                <div className="lg:col-span-1 font-display text-3xl italic text-gold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="lg:col-span-2 hidden lg:block">
                  <div className="relative aspect-[4/3] overflow-hidden border border-ivory/[0.06]">
                    <img
                      src={c.thumb}
                      alt={c.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale-[40%] transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <h3 className="font-display text-3xl leading-tight tracking-tight text-ivory transition-colors group-hover:text-gold lg:text-4xl">
                    {c.title}
                  </h3>
                  <p className="mt-3 max-w-2xl font-sans text-sm text-ivory-muted lg:text-base">
                    {c.summary || c.challenge}
                  </p>
                </div>
                <div className="lg:col-span-2 flex items-center justify-between lg:justify-end lg:gap-6">
                  <div className="font-sans text-[11px] uppercase tracking-widest text-ivory-dim">
                    {c.industry}
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="text-ivory-muted transition-colors group-hover:text-gold"
                  />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
